import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel, Stack } from 'rsuite';
import PageHeader from '@/components/PageHeader';
import RunningClubEventForm from './running-club-event-form';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { RunningClubEvent, UpsertRunningClubEvent } from '@/models/running-club-event.model';
import { getRunningClubEvent } from '@/services/running-club-event.service';
import ActivityInfoList from '@/pages/activity-info/list/activity-info-list';
import RunningClubEventParticipantList from './running-club-event-participant-list';
import RunningClubEventActions from './action/running-club-event-actions';

const CustomNav = ({ active, onSelect, newMode, formObject, ...props }) => {
  return (
    <Stack justifyContent={"space-between"}>
      <Nav {...props} activeKey={active} onSelect={onSelect}>
        <Nav.Item eventKey="running-club-event-form">Running Club Event Detail</Nav.Item>
        <Nav.Item disabled={newMode} eventKey="running-club-event-activities">Activities</Nav.Item>
        <Nav.Item disabled={newMode} eventKey="running-club-event-users">Participants</Nav.Item>
        <Nav.Item disabled={newMode} eventKey="running-club-event-actions">Actions</Nav.Item>
      </Nav>
    </Stack>
  );
};

const Page = () => {

  const requestedTab = useLocation()?.state?.tab;
  const initValue : UpsertRunningClubEvent = useLocation()?.state?.initValue;
  const returnTo : string | undefined = useLocation()?.state?.returnTo;
  const [activeTab, setActiveTab] = useState('running-club-event-form');
  const [formObject, setFormObject] = useState<RunningClubEvent|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;


  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<RunningClubEvent> = await getRunningClubEvent(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching Running Club Event: ${errorModel?.errorMessage}`);
        nav('/routes');
      }
      finally {
        setLoading(false);
      }
    }
  };
 
  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit && formObject ? `Edit Club Event: ${formObject.name}` : 'New Club Event'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href='/running-clubs'> Running Clubs</Breadcrumb.Item>
            <Breadcrumb.Item href='#' onClick={() => {
              const clubId = formObject?.runningClubId ?? initValue?.runningClubId;
              if (returnTo || !clubId) nav(returnTo ?? '/running-clubs/events');
              else nav(`/running-clubs/${clubId}`, {state:{tab: 'running-club-events'}});
            }}> Events</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" 
        active={activeTab} 
        onSelect={key => setActiveTab(key)} 
        newMode={pageState == PageState.New} 
        formObject={formObject}
      />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'running-club-event-form' && <RunningClubEventForm formObject={formObject} initValue={initValue} pageState={pageState} returnTo={returnTo}/>}
          {activeTab == 'running-club-event-activities' && id && <ActivityInfoList runningClubEventId={id}/>}
          {activeTab == 'running-club-event-users' && id && <RunningClubEventParticipantList runningClubEventId={id}/>}
          {activeTab == 'running-club-event-actions' && id && <RunningClubEventActions formObject={formObject} onChanged={fetchData}/>}
        </>
      }
    </Panel>
  );
};

export default Page;
